import { useEffect, useState } from "react";
import axios from "axios";
import {
  ArrowUpRight,
  Plane,
  PlaneTakeoff,
  Globe2,
  Hotel,
  Car,
  Train,
  Calendar,
  Users2,
  Globe,
  MessageCircle,
  Mail,
  CheckCircle2,
  Search,
  FileText,
  Smile,
  Briefcase,
  Building2,
  UserCheck,
  Check,
  X as XIcon,
  Clock,
  Zap,
  TrendingUp,
  ShieldCheck,
  ChevronDown,
  Linkedin,
  Instagram,
  Sparkles,
  GraduationCap,
  Scale,
  Users,
  Menu,
} from "lucide-react";
import { BUSINESS_LOGO_LIGHT_BG, BUSINESS_LOGO_DARK_BG, BUSINESS_PHOTOS, BUSINESS_CONTACT } from "../data/business";
import { ESCAPADAS, ESCAPADAS_COMING_SOON } from "../data/escapadas";
import { publicEscapadasApi } from "../admin/api";
import { Link as RouterLink } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// =============================================================
// Reusable typography
// =============================================================

const Eyebrow = ({ children, light = false }) => (
  <span
    className={`label-eyebrow ${light ? "text-[#E8C97A]" : "text-[#16335A]/70"}`}
  >
    {children}
  </span>
);

const Title = ({ children, light = false, testid, className = "" }) => (
  <h2
    data-testid={testid}
    className={`mt-4 font-display text-4xl lg:text-6xl tracking-tight leading-[1.05] ${
      light ? "text-white" : "text-[#0A192F]"
    } ${className}`}
  >
    {children}
  </h2>
);

// =============================================================
// Header
// =============================================================

const BusinessHeader = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll while mobile menu open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const linkColor = scrolled
    ? "text-[#0A192F] hover:text-[#16335A]"
    : "text-white/95 hover:text-[#E8C97A]";

  const NAV = [
    { id: "solucion", label: "Solución" },
    { id: "escapadas", label: "Escapadas" },
    { id: "partners", label: "Partners" },
    { id: "planes", label: "Planes" },
    { id: "faq", label: "FAQ" },
  ];

  return (
    <header
      data-testid="business-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled || mobileOpen
          ? "bg-[#FDFBF7]/95 backdrop-blur-md border-b border-[#16335A]/10"
          : "bg-gradient-to-b from-[#0A192F]/55 via-[#0A192F]/25 to-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between gap-4">
        <a href="#" onClick={(e) => { e.preventDefault(); setMobileOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="flex items-center" data-testid="business-logo">
          <div className={`font-serif leading-tight text-[0.78rem] sm:text-sm ${scrolled || mobileOpen ? "text-[#0A192F]" : "text-white"}`}>
            <div>Tú diriges el negocio.</div>
            <div className={`italic ${scrolled || mobileOpen ? "text-[#16335A]" : "text-[#E8C97A]"}`}>
              Nosotros organizamos el viaje.
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              data-testid={`business-nav-${l.id}`}
              className={`text-sm font-medium link-underline transition-colors ${linkColor}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="#escapadas"
            data-testid="business-cta-header"
            className={`hidden sm:inline-flex items-center px-5 py-2.5 text-sm font-medium rounded-full transition-all ${
              scrolled
                ? "bg-[#16335A] text-[#FDFBF7] hover:bg-[#214A82]"
                : "bg-[#E8C97A] text-[#16335A] hover:bg-[#D4B35E]"
            }`}
          >
            Nuestras escapadas
          </a>

          {/* Hamburger button — visible on mobile + tablet */}
          <button
            data-testid="mobile-menu-toggle"
            aria-label={mobileOpen ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((v) => !v)}
            className={`lg:hidden p-2 -mr-2 transition-colors ${
              scrolled || mobileOpen ? "text-[#16335A]" : "text-white"
            }`}
          >
            {mobileOpen ? <XIcon size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#FDFBF7] border-t border-[#16335A]/10 max-h-[calc(100vh-5rem)] overflow-y-auto"
        >
          <nav className="px-6 py-6 flex flex-col gap-1">
            {NAV.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={() => setMobileOpen(false)}
                data-testid={`mobile-nav-${l.id}`}
                className="py-4 border-b border-[#16335A]/10 text-[#0A192F] font-display text-xl"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#lanzamiento"
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-cta"
              className="mt-6 bg-[#16335A] text-[#FDFBF7] text-center py-4 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-2"
            >
              Reservar mi plaza
              <ArrowUpRight size={16} />
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

// =============================================================
// 1. HERO
// =============================================================

const Hero = () => (
  <section
    data-testid="business-hero"
    className="relative min-h-[92vh] w-full overflow-hidden pt-20 bg-[#0A192F]"
  >
    <div className="absolute inset-0">
      <img
        src={BUSINESS_PHOTOS.hero}
        alt=""
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F]/95 via-[#0A192F]/75 to-[#0A192F]/40" />
    </div>

    <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-16 lg:pt-20 pb-20 min-h-[92vh] flex flex-col justify-center">
      <div className="mb-12 flex flex-col items-center gap-6">
        <img
          src={BUSINESS_LOGO_DARK_BG}
          alt="Escapafy Business"
          className="h-40 sm:h-52 lg:h-64 w-auto object-contain"
          style={{ mixBlendMode: "screen" }}
        />
      </div>

      <h1
        data-testid="business-hero-title"
        className="font-display text-white text-[2.6rem] sm:text-5xl lg:text-[4.2rem] leading-[1.02] tracking-tight max-w-4xl"
      >
        Tu asistente para viajes{" "}
        <span className="italic text-[#E8C97A]">corporativos,</span>
        <br />
        escapadas y viajes
        <br />
        <span className="italic text-[#E8C97A]">a medida.</span>
      </h1>

      <p className="mt-8 text-white text-lg lg:text-xl font-serif italic max-w-2xl leading-snug text-[#E8C97A]/95">
        Delega la organización de tus viajes y recupera tiempo
        para centrarte en tu negocio.
      </p>

      <p className="mt-6 text-white/80 text-base lg:text-lg max-w-2xl leading-relaxed">
        Nos encargamos de buscar, comparar y organizar tus viajes de negocio
        para que tú puedas centrarte en{" "}
        <span className="text-[#E8C97A] font-medium">
          hacer crecer tu empresa
        </span>
        .
      </p>

      <div className="mt-10 flex flex-wrap items-center gap-4">
        <a
          data-testid="business-hero-cta"
          href="#lanzamiento"
          className="bg-[#E8C97A] text-[#16335A] hover:bg-[#D4B35E] transition-colors px-7 py-3.5 rounded-full text-sm font-semibold inline-flex items-center gap-2"
        >
          Reservar mi plaza
          <ArrowUpRight size={16} />
        </a>
        <a
          href="#solucion"
          className="text-white border border-white/40 hover:border-[#E8C97A] hover:text-[#E8C97A] transition-colors px-7 py-3.5 rounded-full text-sm font-medium"
        >
          Descubrir cómo funciona
        </a>
      </div>

      <div className="mt-14 flex flex-wrap items-center gap-x-10 gap-y-4 text-white/70 text-sm">
        <div className="flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-[#E8C97A]" />
          <span>No somos una agencia de viajes</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-[#E8C97A]" />
          <span>No gestionamos pagos ni tarjetas</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-[#E8C97A]" />
          <span>Comunicación 100% por escrito</span>
        </div>
      </div>
    </div>
  </section>
);

// =============================================================
// 2. PROBLEM
// =============================================================
// 2. PROBLEM → SOLUTION (combined)
// =============================================================

const PROBLEM_ITEMS = [
  { icon: Plane, label: "Vuelos" },
  { icon: Hotel, label: "Hoteles" },
  { icon: Train, label: "Trenes" },
  { icon: Car, label: "Alquileres" },
  { icon: Search, label: "Comparar" },
  { icon: Clock, label: "Horas perdidas" },
];

const STEPS = [
  {
    n: "01",
    title: "Solicitas tu viaje",
    text: "Cuéntanos destino, fechas y necesidades por email o WhatsApp.",
  },
  {
    n: "02",
    title: "Analizamos y comparamos",
    text: "Buscamos las mejores combinaciones de vuelos, hoteles y transporte.",
  },
  {
    n: "03",
    title: "Recibes una propuesta",
    text: "Clara, organizada y adaptada a tu presupuesto y preferencias.",
  },
  {
    n: "04",
    title: "Reservas con el proveedor",
    text: "Mediante enlaces oficiales y seguros. Sin intermediarios de pago.",
  },
];

const ProblemSolution = () => (
  <section
    id="solucion"
    data-testid="problem-solution-section"
    className="relative bg-[#FDFBF7]"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        {/* PROBLEMA — left */}
        <div className="lg:col-span-5">
          <Eyebrow>El problema</Eyebrow>
          <h2
            data-testid="problem-title"
            className="mt-4 font-display text-3xl lg:text-5xl tracking-tight leading-[1.05] text-[#0A192F]"
          >
            ¿Cuánto tiempo pierdes{" "}
            <span className="italic text-[#16335A]">
              organizando viajes de trabajo
            </span>
            ?
          </h2>
          <p className="mt-5 text-[#3A506B] leading-relaxed">
            Buscar vuelos, hoteles, trenes o traslados puede consumir horas cada
            semana. Tiempo que no dedicas a tus clientes ni a tu negocio.
          </p>

          <div className="mt-8 grid grid-cols-3 gap-px bg-[#16335A]/10 border border-[#16335A]/10">
            {PROBLEM_ITEMS.map((it) => {
              const Icon = it.icon;
              return (
                <div
                  key={it.label}
                  className="bg-[#FDFBF7] p-4 flex flex-col items-start gap-2"
                >
                  <Icon size={18} className="text-[#16335A]" />
                  <p className="text-xs text-[#0A192F] leading-tight font-medium">
                    {it.label}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* SOLUCIÓN — right (navy panel) */}
        <div className="lg:col-span-7">
          <div className="relative bg-[#0A192F] text-white p-8 lg:p-10 overflow-hidden" style={{ borderRadius: "2px" }}>
            <div
              aria-hidden
              className="absolute -right-20 -top-20 h-[260px] w-[260px] rounded-full border border-[#E8C97A]/15"
            />
            <div className="relative">
              <Eyebrow light>La solución</Eyebrow>
              <h2
                data-testid="solution-title"
                className="mt-3 font-display text-3xl lg:text-4xl tracking-tight leading-[1.05]"
              >
                Nosotros buscamos.{" "}
                <span className="italic text-[#E8C97A]">Tú decides.</span>
              </h2>
              <p className="mt-5 text-white/75 leading-relaxed text-sm lg:text-base max-w-xl">
                Tú reservas directamente con el proveedor.{" "}
                <span className="text-[#E8C97A] font-medium">
                  Sin intermediarios de pago. Sin datos bancarios.
                </span>
              </p>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#E8C97A]/15 border border-[#E8C97A]/20">
                {STEPS.map((s) => (
                  <div
                    key={s.n}
                    data-testid={`solution-step-${s.n}`}
                    className="bg-[#0A192F] p-5"
                  >
                    <span className="font-display text-[#E8C97A] text-2xl">{s.n}</span>
                    <h3 className="mt-2 font-display text-lg text-white leading-tight">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-white/65 leading-relaxed text-xs">
                      {s.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// =============================================================
// (Removed standalone Solution — now combined with Problem above)
// =============================================================

// =============================================================
// 3. PARTNERS OFICIALES (replaces old Benefits + Why section)
// =============================================================

const PARTNERS = [
  { name: "Iberia", logo: "https://encantify.com/partners/iberia.jpg", href: "https://www.anrdoezrs.net/click-101731687-17295707" },
  { name: "Vueling", logo: "/partners/vueling.png", href: "https://www.anrdoezrs.net/click-101731687-13572630", scale: 0.75 },
  { name: "Transavia", logo: "https://encantify.com/transavia-logo.png", href: "https://click.transavia.com/tickets/?tt=27843_1322932_509073_&r=" },
  { name: "Viajes El Corte Inglés", logo: "https://encantify.com/partners/veci.jpg", href: "https://clk.tradedoubler.com/click?p=260156&a=3480623&url=https%3A%2F%2Fwww.viajeselcorteingles.es%2F" },
  { name: "Booking.com", logo: "https://customer-assets.emergentagent.com/job_smart-chat-app-110/artifacts/tx6ddtew_book-removebg-preview.png", href: "https://www.jdoqocy.com/click-101731687-15734352" },
  { name: "Destinia", logo: "https://encantify.com/partners/destinia.png", href: "https://clk.tradedoubler.com/click?p=17509&a=3480623" },
  { name: "eDreams", logo: "https://encantify.com/partners/edreams.png", href: "https://clk.tradedoubler.com/click?p=15407&a=3480623&g=16409554" },
  { name: "Civitatis", logo: "https://encantify.com/partners/civitatis.jpg", href: "https://www.civitatis.com/es/?aid=112923" },
  { name: "GetYourGuide", logo: "https://encantify.com/partners/gyg.png", href: "https://www.getyourguide.es/?partner_id=TKM0GU7" },
  { name: "Magical Shuttle", logo: "https://encantify.com/partners/magical_shuttle.jpg", href: "https://www.dpbolvw.net/click-101731687-13273632" },
  { name: "IHG Hotels & Resorts", logo: "https://customer-assets-39nsmqrw.emergentagent.net/job_smart-chat-app-110/artifacts/6hhqt4kz_ihg-removebg-preview.png", href: "https://www.jdoqocy.com/click-101731687-15734302" },
  { name: "Barceló Hotels & Resorts", logo: "https://customer-assets-39nsmqrw.emergentagent.net/job_smart-chat-app-110/artifacts/3bqb1d34_bar-removebg-preview.png", href: "https://clk.tradedoubler.com/click?p=269827&a=3480623" },
  { name: "Radisson Hotels", logo: "https://customer-assets-39nsmqrw.emergentagent.net/job_smart-chat-app-110/artifacts/vagwr0ku_rad-removebg-preview.png", href: "https://clk.tradedoubler.com/click?p=344536&a=3480623", scale: 1.35 },
  { name: "IATI Seguros", logo: "https://customer-assets.emergentagent.com/job_smart-chat-app-110/artifacts/ff3d27bs_IATI.png", href: "https://clk.tradedoubler.com/click?p=403039&a=3480623&g=26173470" },
  { name: "Europcar", logo: "/partners/europcar.png", href: "https://www.awin1.com/cread.php?awinmid=10624&awinaffid=2874469&campaign=PROMO+CODE+EUROPCAR+Q3&ued=https%3A%2F%2Fwww.europcar.es%2Fes-es%3Futm_source%3Dgoogle%26utm_source_platform%3DSA360%26utm_medium%3Dcpc%26utm_campaign%3D%255BGOO%255D-%255BSPA%255D-%255BCar%255D-%255BSource_ES%255D-%255BDestination_ES%255D-%255Beuropcar.es%255D-%255BBrand_Core%255D-%255BPFX%255D%26utm_id%3D8432499760%26utm_content%3D86028311477%26gclsrc%3Daw.ds%26gad_source%3D1%26gad_campaignid%3D8432499760%26gbraid%3D0AAAAADsbLR0aKe5qJeC-GGdjeD6UroZsJ%26gclid%3DCjwKCAjwqJXUBhBNEiwA8BgG7r4wVR1MWCMqxAcxiNfg-HHjg-u2IkIqZ04hfS4wxqeTKdwmMZ6SvxoCGwMQAvD_BwE" },
];

const PartnersSection = () => (
  <section
    id="partners"
    data-testid="partners-section"
    className="relative bg-[#FDFBF7] py-20 lg:py-28 overflow-hidden"
  >
    <div
      aria-hidden
      className="absolute -right-32 -top-32 h-[420px] w-[420px] rounded-full border border-[#16335A]/10"
    />
    <div
      aria-hidden
      className="absolute -left-40 bottom-0 h-[320px] w-[320px] rounded-full border border-[#16335A]/[0.06]"
    />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
      <div className="max-w-3xl">
        <Eyebrow>Partners Oficiales</Eyebrow>
        <h2
          data-testid="partners-title"
          className="mt-3 font-display text-3xl lg:text-5xl tracking-tight leading-[1.05] text-[#0A192F]"
        >
          Colaboramos con quienes{" "}
          <span className="italic text-[#16335A]">mejor conocen el sector.</span>
        </h2>
        <p className="mt-6 text-[#3A506B] text-base lg:text-lg leading-relaxed max-w-2xl">
          En Escapafy colaboramos con algunas de las compañías más reconocidas del sector turístico.
          Esto nos permite ofrecerte una amplia selección de vuelos, hoteles, traslados, actividades
          y seguros, siempre reservando directamente con el proveedor oficial.
        </p>
      </div>

      <ul
        data-testid="partners-grid"
        className="mt-14 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 lg:gap-5"
      >
        {PARTNERS.map((p) => (
          <li key={p.name}>
            <a
              href={p.href}
              target="_blank"
              rel="noopener noreferrer sponsored"
              data-testid={`partner-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
              title={p.name}
              className="group relative flex items-center justify-center h-24 lg:h-28 bg-white border border-[#16335A]/10 hover:border-[#E8C97A] hover:shadow-[0_10px_30px_rgba(10,25,47,0.08)] transition-all duration-300"
              style={{ borderRadius: "2px" }}
            >
              <img
                src={p.logo}
                alt={p.name}
                loading="lazy"
                style={p.scale ? { maxHeight: `${4 * p.scale}rem`, maxWidth: `${Math.min(90, 80 * p.scale)}%` } : undefined}
                className="max-h-14 lg:max-h-16 max-w-[80%] object-contain transition-transform duration-300 ease-out group-hover:scale-110"
              />
            </a>
          </li>
        ))}
      </ul>

      <p className="mt-10 text-[#3A506B]/70 text-xs italic max-w-2xl leading-relaxed">
        Trabajamos con proveedores oficiales de las principales redes de afiliación
        (Awin, Tradedoubler, CJ). Tú reservas siempre al precio oficial, sin recargos.
      </p>

      {/* CJ impression pixels (Vueling) */}
      <img src="https://www.tqlkg.com/image-101731687-13572630" width="1" height="1" alt="" style={{ position: "absolute", left: "-9999px" }} aria-hidden="true" />
      {/* Tradedoubler impression pixel (IATI) */}
      <img src="https://imp.tradedoubler.com/imp?type(img)g(26173470)a(3480623)44642845" width="1" height="1" alt="" style={{ position: "absolute", left: "-9999px" }} aria-hidden="true" />
    </div>
  </section>
);

// =============================================================
// (Removed WhyEscapafy — now merged into BenefitsAndWhy above)
// =============================================================

// =============================================================
// 4. ESCAPADAS LOW COST — editorial cards linking to full guides
// =============================================================

const EscapadasSection = () => {
  const [dbItems, setDbItems] = useState([]);
  useEffect(() => {
    publicEscapadasApi.list().then(setDbItems).catch(() => setDbItems([]));
  }, []);
  const staticSlugs = new Set(ESCAPADAS.map((e) => e.slug));
  // Merge: static first, then DB items not already in static
  const allActive = [
    ...ESCAPADAS,
    ...dbItems.filter((d) => !staticSlugs.has(d.slug)),
  ];
  return (
    <section
      id="escapadas"
      data-testid="escapadas-section"
      className="relative bg-[#F3EFE7] py-20 lg:py-28 overflow-hidden"
    >
      <div aria-hidden className="absolute -left-40 top-20 h-[380px] w-[380px] rounded-full border border-[#16335A]/[0.08]" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <Eyebrow>Escapadas Low Cost</Eyebrow>
            <h2
              data-testid="escapadas-title"
              className="mt-3 font-display text-3xl lg:text-5xl tracking-tight leading-[1.05] text-[#0A192F]"
            >
              Escapadas reales, {" "}
              <span className="italic text-[#16335A]">contadas por nosotras.</span>
            </h2>
            <p className="mt-6 text-[#3A506B] text-base lg:text-lg leading-relaxed">
              Fines de semana probados, con vuelos, hoteles y experiencias que ya hemos disfrutado.
              Cada guía incluye enlaces directos a los proveedores oficiales — reservas tú, al mejor precio.
            </p>
          </div>
        </div>

        <ul
          data-testid="escapadas-grid"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
        >
          {allActive.map((e) => (
            <li key={e.slug}>
              <RouterLink
                to={`/escapadas/${e.slug}`}
                data-testid={`escapada-card-${e.slug}`}
                className="group block relative bg-white border border-[#EAE3D5] hover:border-[#E8C97A] hover:shadow-[0_20px_60px_rgba(10,25,47,0.12)] transition-all duration-500 overflow-hidden"
                style={{ borderRadius: "3px" }}
              >
                <div className="aspect-[4/3] overflow-hidden bg-[#EAE3D5]">
                  <img
                    src={e.hero_image}
                    alt={e.card_title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
                <div className="p-6 lg:p-7">
                  <p className="text-[#3A506B] text-[0.65rem] tracking-[0.3em] uppercase font-semibold mb-2">
                    {e.country || "Escapada"}
                  </p>
                  <h3 className="font-display text-2xl lg:text-3xl text-[#0A192F] tracking-tight leading-tight">
                    {e.card_title}
                  </h3>
                  <p className="mt-2 text-[#3A506B] text-sm">{e.card_subtitle}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-[#0A192F] text-xs tracking-[0.2em] uppercase font-semibold group-hover:text-[#16335A] transition-colors">
                    Leer escapada <ArrowUpRight size={12} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </RouterLink>
            </li>
          ))}
          {ESCAPADAS_COMING_SOON.map((e, i) => (
            <li key={`coming-${i}`}>
              <div
                data-testid={`escapada-coming-${i}`}
                className="relative bg-white/60 border border-dashed border-[#16335A]/25 overflow-hidden"
                style={{ borderRadius: "3px" }}
              >
                <div className="aspect-[4/3] overflow-hidden bg-[#EAE3D5]">
                  <img
                    src={e.hero_image}
                    alt={e.card_title}
                    loading="lazy"
                    className="w-full h-full object-cover opacity-40 grayscale"
                  />
                </div>
                <div className="p-6 lg:p-7">
                  <p className="text-[#E8C97A] text-[0.65rem] tracking-[0.3em] uppercase font-semibold mb-2">Próximamente</p>
                  <h3 className="font-display text-2xl lg:text-3xl text-[#0A192F]/60 tracking-tight leading-tight">
                    {e.card_title}
                  </h3>
                  <p className="mt-2 text-[#3A506B]/60 text-sm">{e.card_subtitle}</p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

// =============================================================
// 5. FOR WHOM
// =============================================================

const PROFILES = [
  {
    icon: UserCheck,
    title: "Autónomos y profesionales independientes",
    short: "Autónomos",
    text: "Consultores, formadores, asesores y profesionales que viajan por trabajo y quieren dejar de perder tiempo organizando desplazamientos.",
  },
  {
    icon: Zap,
    title: "Emprendedores y startups",
    short: "Startups",
    text: "Negocios en crecimiento que necesitan una solución profesional para gestionar sus viajes sin contratar personal adicional.",
  },
  {
    icon: Scale,
    title: "Despachos profesionales",
    short: "Despachos",
    text: "Abogados, asesores, gestores, arquitectos y consultoras que prefieren centrarse en sus clientes mientras delegan la logística de sus viajes.",
  },
  {
    icon: Briefcase,
    title: "Directivos con agendas exigentes",
    short: "Directivos",
    text: "Personas que valoran su tiempo y buscan delegar la organización de sus desplazamientos para centrarse en tareas estratégicas.",
  },
  {
    icon: Building2,
    title: "Pequeñas empresas",
    short: "PYMES",
    text: "Empresas de hasta 50 empleados que necesitan apoyo en la gestión de viajes corporativos sin disponer de un departamento interno especializado.",
  },
];

const ForWhom = () => {
  const [active, setActive] = useState(0);
  const profile = PROFILES[active];
  const ActiveIcon = profile.icon;

  return (
    <section
      data-testid="for-whom-section"
      className="relative bg-[#FDFBF7] py-20 lg:py-28"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="max-w-3xl mb-10">
          <Eyebrow>Para quién es</Eyebrow>
          <Title testid="for-whom-title">
            ¿Para quién está pensado
            <br />
            <span className="italic text-[#16335A]">Escapafy Business?</span>
          </Title>
          <p className="mt-5 text-[#3A506B] leading-relaxed">
            Si tu tiempo es valioso y organizar viajes te quita horas cada mes,
            Escapafy Business es para ti.
          </p>
        </div>

        {/* Pill selector */}
        <div className="flex flex-wrap gap-2 mb-8">
          {PROFILES.map((p, i) => (
            <button
              key={p.short}
              onClick={() => setActive(i)}
              data-testid={`profile-pill-${i}`}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all border ${
                active === i
                  ? "bg-[#16335A] text-[#E8C97A] border-[#16335A]"
                  : "bg-white text-[#0A192F] border-[#16335A]/15 hover:border-[#16335A]/40"
              }`}
            >
              {p.short}
            </button>
          ))}
        </div>

        {/* Active profile detail */}
        <div
          data-testid="profile-detail"
          className="bg-white border border-[#16335A]/15 p-8 lg:p-10"
          style={{ borderRadius: "2px" }}
        >
          <div className="flex items-start gap-5">
            <div className="h-14 w-14 shrink-0 rounded-full bg-[#16335A] text-[#E8C97A] flex items-center justify-center">
              <ActiveIcon size={24} />
            </div>
            <div className="flex-1">
              <h3 className="font-display text-2xl lg:text-3xl text-[#0A192F] leading-tight">
                {profile.title}
              </h3>
              <p className="mt-3 text-[#3A506B] leading-relaxed text-base">
                {profile.text}
              </p>
            </div>
          </div>
        </div>

        {/* Highlighted footer block */}
        <div
          data-testid="for-whom-highlight"
          className="mt-12 bg-[#0A192F] text-white p-8 lg:p-10 relative overflow-hidden"
          style={{ borderRadius: "2px" }}
        >
          <div
            aria-hidden
            className="absolute -right-24 -top-24 h-[220px] w-[220px] rounded-full border border-[#E8C97A]/15"
          />
          <div className="relative max-w-4xl">
            <p className="text-white/90 text-base lg:text-lg leading-relaxed">
              Escapafy Business está diseñado para empresas y profesionales que
              necesitan el apoyo de una{" "}
              <span className="text-[#E8C97A] font-medium">
                Executive Assistant para la gestión de viajes
              </span>
              , sin contratar una a tiempo completo.
            </p>
            <p className="mt-5 font-serif italic text-[#E8C97A] text-lg leading-snug border-l-2 border-[#E8C97A] pl-5">
              Mi objetivo es ayudarte a ahorrar tiempo, simplificar la
              organización de tus desplazamientos y ofrecerte la tranquilidad
              de contar con un apoyo profesional de confianza.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// =============================================================
// 6. SERVICES
// =============================================================

const SERVICES = [
  { icon: Plane, label: "Vuelos nacionales e internacionales" },
  { icon: Hotel, label: "Hoteles" },
  { icon: Train, label: "Trenes" },
  { icon: Car, label: "Traslados" },
  { icon: Car, label: "Alquiler de vehículos" },
  { icon: Briefcase, label: "Viajes corporativos" },
  { icon: Calendar, label: "Eventos y reuniones" },
  { icon: Globe, label: "Viajes recurrentes" },
  { icon: Sparkles, label: "Escapadas e incentivos para equipos" },
];

const Services = () => (
  <section
    id="servicios"
    data-testid="services-section"
    className="relative bg-[#0A192F] text-white py-24 lg:py-32"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="max-w-3xl mb-14">
        <Eyebrow light>Servicios</Eyebrow>
        <Title light testid="services-title">
          ¿Qué podemos
          <br />
          <span className="italic text-[#E8C97A]">gestionar por ti?</span>
        </Title>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E8C97A]/15 border border-[#E8C97A]/20">
        {SERVICES.map((s, i) => {
          const Icon = s.icon;
          return (
            <div
              key={i}
              data-testid={`service-${i}`}
              className="bg-[#0A192F] p-6 lg:p-8 flex items-center gap-4 hover:bg-[#0F2342] transition-colors"
            >
              <Icon size={20} className="text-[#E8C97A] shrink-0" />
              <p className="text-white/90 leading-tight">{s.label}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

// =============================================================
// 7. PLANS
// =============================================================

const PLANS = [
  {
    id: "boarding",
    name: "Boarding",
    price: "49 €",
    period: "/mes",
    icon: PlaneTakeoff,
    pitch: "Ideal para autónomos y pequeñas empresas.",
    featured: false,
    cta: "Reservar mi plaza",
    trial: true,
    features: [
      "Hasta 4 solicitudes mensuales",
      "Comparativas personalizadas",
      "Búsqueda de vuelos, hoteles y transporte",
      "Propuestas organizadas",
      "Soporte por email",
    ],
  },
  {
    id: "business-class",
    name: "Business Class",
    price: "99 €",
    period: "/mes",
    icon: Plane,
    pitch: "Para empresas con desplazamientos frecuentes.",
    featured: true,
    cta: "Solicitar acceso",
    trial: false,
    features: [
      "Hasta 12 solicitudes mensuales",
      "Prioridad media",
      "Seguimiento de reservas",
      "Soporte por email y WhatsApp",
      "Gestión de cambios sencillos",
    ],
  },
  {
    id: "first-class",
    name: "First Class",
    price: "199 €",
    period: "/mes",
    icon: Globe2,
    pitch: "Para empresas que desean delegar completamente.",
    featured: false,
    cta: "Solicitar acceso",
    trial: false,
    features: [
      "Solicitudes ilimitadas (uso razonable)",
      "Prioridad máxima",
      "Gestión de incidencias",
      "Búsquedas avanzadas personalizadas",
      "Soporte prioritario",
      "Atención personalizada",
    ],
  },
];

const Plans = () => (
  <section
    id="planes"
    data-testid="plans-section"
    className="relative bg-[#FDFBF7] py-24 lg:py-32"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="max-w-3xl mb-14">
        <Eyebrow>Planes</Eyebrow>
        <Title testid="plans-title">
          Elige la experiencia
          <br />
          <span className="italic text-[#16335A]">
            que mejor se adapte a tu empresa.
          </span>
        </Title>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        {PLANS.map((p) => (
          <div
            key={p.id}
            data-testid={`plan-${p.id}`}
            className={`relative flex flex-col p-8 lg:p-10 border transition-all ${
              p.featured
                ? "bg-[#0A192F] text-white border-[#E8C97A] lg:-mt-4 lg:mb-4 shadow-2xl"
                : "bg-white text-[#0A192F] border-[#16335A]/15 hover:border-[#16335A]/40"
            }`}
            style={{ borderRadius: "2px" }}
          >
            {p.featured && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#E8C97A] text-[#16335A] px-4 py-1 rounded-full text-xs font-semibold tracking-wider uppercase">
                Más elegido
              </span>
            )}
            {p.trial && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#16335A] text-[#E8C97A] px-4 py-1 rounded-full text-xs font-semibold tracking-wider uppercase">
                Oferta de lanzamiento
              </span>
            )}

            {(() => {
              const Icon = p.icon;
              return (
                <Icon
                  size={40}
                  strokeWidth={1.2}
                  className={p.featured ? "text-[#E8C97A]" : "text-[#16335A]"}
                />
              );
            })()}
            <h3
              className={`mt-5 font-display text-3xl tracking-tight ${
                p.featured ? "text-white" : "text-[#0A192F]"
              }`}
            >
              {p.name}
            </h3>
            <p
              className={`mt-3 text-sm leading-relaxed ${
                p.featured ? "text-white/70" : "text-[#3A506B]"
              }`}
            >
              {p.pitch}
            </p>

            <div className="mt-8 flex items-baseline gap-1">
              <span
                className={`font-display text-5xl tracking-tight ${
                  p.featured ? "text-[#E8C97A]" : "text-[#16335A]"
                }`}
              >
                {p.price}
              </span>
              <span
                className={`${p.featured ? "text-white/60" : "text-[#3A506B]"}`}
              >
                {p.period}
              </span>
            </div>

            <ul
              className={`mt-8 space-y-3 flex-1 ${
                p.featured ? "text-white/90" : "text-[#0A192F]/85"
              }`}
            >
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <Check
                    size={18}
                    className={`shrink-0 mt-0.5 ${
                      p.featured ? "text-[#E8C97A]" : "text-[#16335A]"
                    }`}
                    strokeWidth={2.5}
                  />
                  <span className="leading-relaxed text-sm">{f}</span>
                </li>
              ))}
            </ul>

            <a
              href="#lanzamiento"
              data-testid={`plan-cta-${p.id}`}
              className={`mt-10 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-colors ${
                p.featured
                  ? "bg-[#E8C97A] text-[#16335A] hover:bg-[#D4B35E]"
                  : "bg-[#16335A] text-white hover:bg-[#214A82]"
              }`}
            >
              {p.cta}
              <ArrowUpRight size={16} />
            </a>
          </div>
        ))}
      </div>

      <p className="mt-10 text-center text-[#3A506B] text-sm italic font-serif">
        Programa Founders: condiciones preferentes para las primeras empresas —{" "}
        <a
          href="#lanzamiento"
          className="text-[#16335A] underline underline-offset-4 decoration-[#E8C97A]"
        >
          solicita acceso aquí
        </a>
        .
      </p>
    </div>
  </section>
);

// =============================================================
// 7.5 — FOUNDER (Mercè)
// =============================================================

const Founder = () => (
  <section
    data-testid="founder-section"
    className="relative bg-[#FDFBF7] py-20 lg:py-28"
  >
    <div className="max-w-4xl mx-auto px-6 lg:px-12">
      <Eyebrow>La persona detrás del servicio</Eyebrow>
      <Title testid="founder-title">
        Hola, soy{" "}
        <span className="italic text-[#16335A]">Mercè.</span>
      </Title>
      <div className="mt-8 space-y-5 text-[#3A506B] text-base lg:text-lg leading-relaxed">
        <p>
          <strong className="text-[#0A192F] font-medium">
            Executive Assistant
          </strong>{" "}
          con experiencia organizando viajes, agendas y desplazamientos
          para CEOs, directivos y equipos.
        </p>
        <p>
          Creé{" "}
          <strong className="text-[#0A192F] font-medium">
            Escapafy Business
          </strong>{" "}
          para ayudar a pequeñas empresas a ahorrar tiempo y simplificar
          la gestión de sus viajes.
        </p>
        <p className="pt-3 font-serif italic text-xl lg:text-2xl text-[#16335A] leading-snug border-l-2 border-[#E8C97A] pl-6">
          &ldquo;Mi objetivo es ayudarte a ahorrar tiempo y simplificar
          cada desplazamiento, para que puedas centrarte en lo que
          realmente importa: tu empresa.&rdquo;
        </p>
      </div>

      <a
        href="#lanzamiento"
        data-testid="founder-cta"
        className="mt-10 inline-flex items-center gap-2 bg-[#16335A] text-white hover:bg-[#214A82] transition-colors px-7 py-3.5 rounded-full text-sm font-semibold"
      >
        Reservar mi plaza
        <ArrowUpRight size={16} />
      </a>
    </div>
  </section>
);

// =============================================================
// 8. FAQ
// =============================================================

const FAQS = [
  {
    q: "¿Escapafy Business realiza las reservas?",
    a: "No. El cliente reserva directamente con el proveedor mediante enlaces oficiales.",
  },
  {
    q: "¿Tengo que compartir mis datos bancarios?",
    a: "No. Los pagos se realizan directamente con cada proveedor.",
  },
  {
    q: "¿Puedo solicitar viajes nacionales e internacionales?",
    a: "Sí. Gestionamos tanto desplazamientos nacionales como internacionales.",
  },
  {
    q: "¿Está pensado solo para empresas?",
    a: "También para autónomos y profesionales que viajan por trabajo.",
  },
  {
    q: "¿Cuánto tiempo tardáis en enviar propuestas?",
    a: "Intentamos responder en menos de 2 horas.",
  },
];

const FAQ = () => {
  const [open, setOpen] = useState(0);
  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative bg-[#FDFBF7] py-24 lg:py-32"
    >
      <div className="max-w-4xl mx-auto px-6 lg:px-12">
        <div className="mb-12">
          <Eyebrow>Preguntas frecuentes</Eyebrow>
          <Title testid="faq-title">
            Todo lo que querías
            <br />
            <span className="italic text-[#16335A]">preguntarnos.</span>
          </Title>
        </div>
        <div className="divide-y divide-[#16335A]/10 border-y border-[#16335A]/10">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={i} data-testid={`faq-item-${i}`}>
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full py-6 flex items-center justify-between gap-6 text-left group"
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="font-display text-lg lg:text-xl text-[#0A192F] group-hover:text-[#16335A] transition-colors">
                    {f.q}
                  </span>
                  <ChevronDown
                    size={20}
                    className={`shrink-0 text-[#16335A] transition-transform ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isOpen && (
                  <div className="pb-6 text-[#3A506B] leading-relaxed">
                    {f.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// =============================================================
// 10. PILOTO FINAL CTA + FORM
// =============================================================

const FOUNDERS_BENEFITS = [
  "Atención personalizada",
  "Acceso anticipado al servicio",
  "Condiciones preferentes de lanzamiento",
  "Participación directa en la mejora del servicio",
];

const initialForm = {
  nombre: "",
  empresa: "",
  cargo: "",
  email: "",
  num_empleados: "",
  comentarios: "",
};

const Field = ({ label, name, type = "text", required, light = false, ...rest }) => (
  <label className="block">
    <span
      className={`block text-xs font-medium mb-2 uppercase tracking-wider ${
        light ? "text-[#E8C97A]" : "text-[#16335A]"
      }`}
    >
      {label} {required && <span className="text-[#E8C97A]">*</span>}
    </span>
    <input
      type={type}
      name={name}
      required={required}
      data-testid={`field-${name}`}
      {...rest}
      className={`w-full px-4 py-3 text-sm transition-all border focus:outline-none focus:ring-2 focus:ring-[#E8C97A]/40 ${
        light
          ? "bg-white/5 border-white/20 focus:border-[#E8C97A] text-white placeholder-white/40"
          : "bg-white border-[#16335A]/20 focus:border-[#16335A] text-[#0A192F]"
      }`}
      style={{ borderRadius: "2px" }}
    />
  </label>
);

const TextArea = ({ label, name, light = false, ...rest }) => (
  <label className="block">
    <span
      className={`block text-xs font-medium mb-2 uppercase tracking-wider ${
        light ? "text-[#E8C97A]" : "text-[#16335A]"
      }`}
    >
      {label}
    </span>
    <textarea
      name={name}
      data-testid={`field-${name}`}
      {...rest}
      className={`w-full px-4 py-3 text-sm transition-all border focus:outline-none focus:ring-2 focus:ring-[#E8C97A]/40 resize-y ${
        light
          ? "bg-white/5 border-white/20 focus:border-[#E8C97A] text-white placeholder-white/40"
          : "bg-white border-[#16335A]/20 focus:border-[#16335A] text-[#0A192F]"
      }`}
      style={{ borderRadius: "2px" }}
    />
  </label>
);

const Select = ({ label, name, options, light = false, ...rest }) => (
  <label className="block">
    <span
      className={`block text-xs font-medium mb-2 uppercase tracking-wider ${
        light ? "text-[#E8C97A]" : "text-[#16335A]"
      }`}
    >
      {label}
    </span>
    <select
      name={name}
      data-testid={`field-${name}`}
      {...rest}
      className={`w-full px-4 py-3 text-sm transition-all border focus:outline-none focus:ring-2 focus:ring-[#E8C97A]/40 ${
        light
          ? "bg-[#0A192F] border-white/20 focus:border-[#E8C97A] text-white"
          : "bg-white border-[#16335A]/20 focus:border-[#16335A] text-[#0A192F]"
      }`}
      style={{ borderRadius: "2px" }}
    >
      {options.map((o) => (
        <option key={o.v} value={o.v}>
          {o.l}
        </option>
      ))}
    </select>
  </label>
);

const FoundersForm = () => {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim() || !form.empresa.trim() || !form.email.trim()) {
      setError("Por favor completa nombre, empresa y email.");
      setStatus("error");
      return;
    }
    setStatus("loading");
    setError(null);
    try {
      await axios.post(`${API}/business-leads`, form);
      setStatus("success");
      setForm(initialForm);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg;
      if (Array.isArray(detail)) {
        msg = detail.map((d) => (typeof d === "object" ? d.msg || JSON.stringify(d) : String(d))).join(" · ");
      } else if (typeof detail === "string") {
        msg = detail;
      } else {
        msg = "No hemos podido registrar tu solicitud. Inténtalo de nuevo en un momento.";
      }
      setError(msg);
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div
        data-testid="lead-success"
        className="bg-white/5 backdrop-blur-md border border-[#E8C97A]/30 p-10 lg:p-14 text-center"
        style={{ borderRadius: "2px" }}
      >
        <div className="mx-auto h-16 w-16 rounded-full bg-[#E8C97A] text-[#16335A] flex items-center justify-center">
          <CheckCircle2 size={28} />
        </div>
        <h3 className="mt-6 font-display text-3xl lg:text-4xl text-white">
          ¡Solicitud recibida!
        </h3>
        <p className="mt-4 text-white/80 max-w-md mx-auto leading-relaxed">
          Hemos registrado tu solicitud para el programa de lanzamiento. Te contactaremos
          por email o WhatsApp en las próximas horas.
        </p>
        <button
          onClick={() => setStatus("idle")}
          data-testid="lead-success-reset"
          className="mt-8 text-[#E8C97A] font-medium link-underline"
        >
          Enviar otra solicitud
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="lead-form"
      className="bg-white/5 backdrop-blur-md border border-[#E8C97A]/20 p-8 lg:p-12 space-y-6"
      style={{ borderRadius: "2px" }}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Nombre" name="nombre" required value={form.nombre} onChange={handleChange} light />
        <Field label="Empresa" name="empresa" required value={form.empresa} onChange={handleChange} light />
        <Field label="Cargo" name="cargo" value={form.cargo} onChange={handleChange} light />
        <Field label="Email" name="email" type="email" required value={form.email} onChange={handleChange} light />
        <Select
          label="Número de empleados"
          name="num_empleados"
          value={form.num_empleados}
          onChange={handleChange}
          light
          options={[
            { v: "", l: "Selecciona..." },
            { v: "1-5", l: "1-5 (Autónomo / micro)" },
            { v: "6-20", l: "6-20" },
            { v: "21-50", l: "21-50" },
            { v: "50+", l: "Más de 50" },
          ]}
        />
      </div>
      <TextArea label="Comentarios" name="comentarios" rows={4} value={form.comentarios} onChange={handleChange} light />

      {status === "error" && (
        <p
          data-testid="lead-error"
          className="text-red-100 bg-red-900/40 border border-red-400/40 px-4 py-3 text-sm"
        >
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "loading"}
        data-testid="lead-submit"
        className="w-full sm:w-auto bg-[#E8C97A] hover:bg-[#D4B35E] disabled:opacity-60 disabled:cursor-not-allowed text-[#16335A] px-8 py-4 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors"
      >
        {status === "loading" ? "Enviando..." : "Reservar mi plaza"}
        <ArrowUpRight size={16} />
      </button>
      <p className="text-xs text-white/55 leading-relaxed">
        Al enviar este formulario aceptas que te contactemos por email o WhatsApp en relación con el programa de lanzamiento. No compartimos tus datos con terceros.
      </p>
    </form>
  );
};

const FoundersCTA = () => (
  <section
    id="lanzamiento"
    data-testid="founders-section"
    className="relative bg-[#0A192F] text-white py-24 lg:py-32 overflow-hidden"
  >
    <div
      aria-hidden
      className="absolute -left-40 -top-40 h-[520px] w-[520px] rounded-full border border-[#E8C97A]/15"
    />
    <div
      aria-hidden
      className="absolute -right-32 bottom-0 h-[320px] w-[320px] rounded-full border border-[#E8C97A]/10"
    />

    <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <span className="inline-flex items-center gap-2 bg-[#E8C97A] text-[#16335A] px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider">
            <Sparkles size={14} /> Socios de lanzamiento
          </span>
          <Title light testid="founders-title">
            Únete a los Socios
            <br />
            <span className="italic text-[#E8C97A]">de lanzamiento.</span>
          </Title>

          <p className="mt-6 text-white text-lg font-serif italic leading-snug max-w-xl">
            Estamos seleccionando las primeras{" "}
            <span className="text-[#E8C97A] not-italic font-semibold">
              10 empresas
            </span>{" "}
            que quieran participar en el lanzamiento de Escapafy Business.
          </p>

          <p className="mt-6 text-white/75 leading-relaxed max-w-xl">
            Tendrán <span className="text-[#E8C97A] font-medium">acceso
            anticipado al servicio</span>, atención personalizada y condiciones
            preferentes de lanzamiento.
          </p>

          <p className="mt-6 font-serif italic text-[#E8C97A] text-lg">
            Plazas limitadas.
          </p>

          <ul className="mt-8 space-y-3">
            {FOUNDERS_BENEFITS.map((b) => (
              <li
                key={b}
                className="flex items-start gap-3 text-white/90"
              >
                <CheckCircle2 size={18} className="text-[#E8C97A] shrink-0 mt-0.5" />
                <span className="leading-relaxed">{b}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8 flex gap-6 text-sm">
            <span className="text-[#E8C97A]">Sin permanencia.</span>
            <span className="text-[#E8C97A]">Sin compromiso.</span>
          </div>

          <p className="mt-12 font-display text-3xl lg:text-4xl text-white leading-tight">
            Tú diriges el negocio.
            <br />
            <span className="italic text-[#E8C97A]">
              Nosotros organizamos el viaje.
            </span>
          </p>
        </div>

        <div className="lg:col-span-7">
          <FoundersForm />
        </div>
      </div>
    </div>
  </section>
);

// =============================================================
// FOOTER
// =============================================================

const BusinessFooter = () => (
  <footer
    data-testid="business-footer"
    className="bg-[#0A192F] text-[#FDFBF7] py-14 border-t border-[#E8C97A]/15"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
        <div>
          <img
            src={BUSINESS_LOGO_DARK_BG}
            alt="Escapafy Business"
            className="h-32 lg:h-40 w-auto object-contain -ml-4"
            style={{ mixBlendMode: "screen" }}
          />
          <p className="mt-4 text-white/60 text-sm leading-relaxed max-w-xs">
            Tu asistente de viajes corporativos para pequeñas empresas y profesionales.
          </p>
        </div>

        <div>
          <h4 className="font-display text-lg text-white mb-4">Información</h4>
          <ul className="space-y-2 text-sm text-white/70">
            <li><a href="#aviso-legal" className="hover:text-[#E8C97A] transition-colors">Aviso legal</a></li>
            <li><a href="#privacidad" className="hover:text-[#E8C97A] transition-colors">Política de privacidad</a></li>
            <li><a href="#faq" className="hover:text-[#E8C97A] transition-colors">Preguntas frecuentes</a></li>
          </ul>
        </div>

        <div>
          <h4 className="font-display text-lg text-white mb-4">Contacto</h4>
          <ul className="space-y-3 text-sm">
            <li>
              <a
                href={`mailto:${BUSINESS_CONTACT.email}`}
                className="text-white/70 hover:text-[#E8C97A] transition-colors inline-flex items-center gap-2"
              >
                <Mail size={14} />
                {BUSINESS_CONTACT.email}
              </a>
            </li>
            <li>
              <a
                href={`https://wa.me/${BUSINESS_CONTACT.whatsapp.replace(/[^\d]/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/70 hover:text-[#E8C97A] transition-colors inline-flex items-center gap-2"
              >
                <MessageCircle size={14} /> WhatsApp
              </a>
            </li>
          </ul>
          <div className="mt-5 flex items-center gap-3">
            <a
              href={BUSINESS_CONTACT.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-linkedin"
              aria-label="LinkedIn"
              className="h-9 w-9 rounded-full border border-white/20 hover:border-[#E8C97A] hover:text-[#E8C97A] text-white/70 flex items-center justify-center transition-colors"
            >
              <Linkedin size={15} />
            </a>
            <a
              href={BUSINESS_CONTACT.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram"
              aria-label="Instagram"
              className="h-9 w-9 rounded-full border border-white/20 hover:border-[#E8C97A] hover:text-[#E8C97A] text-white/70 flex items-center justify-center transition-colors"
            >
              <Instagram size={15} />
            </a>
          </div>
        </div>
      </div>

      <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs text-white/50">
        <div>© {new Date().getFullYear()} Escapafy. Todos los derechos reservados.</div>
        <a href="#lanzamiento" className="hover:text-[#E8C97A] transition-colors">
          Solicita acceso al programa Founders
        </a>
      </div>
    </div>
  </footer>
);

// =============================================================
// PAGE
// =============================================================

export default function BusinessLanding() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="business-landing" className="min-h-screen bg-[#FDFBF7]">
      <BusinessHeader />
      <main>
        <Hero />
        <PartnersSection />
        <ProblemSolution />
        <EscapadasSection />
        <ForWhom />
        <Services />
        <Plans />
        <Founder />
        <FAQ />
        <FoundersCTA />
      </main>
      <BusinessFooter />
    </div>
  );
}
